import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import {
  Package, CurrencyDollar, Cube, ArrowsClockwise,
  Warning, TrendUp, TrendDown, ArrowRight, Lightning,
  CheckCircle, Circle, ArrowUpRight, Truck, ChartLine, RocketLaunch
} from '@phosphor-icons/react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSellerMode } from '../contexts/SellerModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  accepted: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
  packed: { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-400' },
  shipped: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-400' },
  delivered: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-400' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, color, trend, trendLabel }) {
  return (
    <div className="kpi-card group" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-[#9c9db7] uppercase tracking-wider mb-1">{label}</p>
          <p className="text-2xl sm:text-[1.75rem] font-extrabold text-[#0A0A0A] leading-tight tracking-tight">{value}</p>
          {subtitle && <p className="mt-1.5 text-[12px] text-[#d18f08] font-medium">{subtitle}</p>}
          {trend !== undefined && (
            <div className="flex items-center gap-1 mt-2">
              {trend >= 0 ? (
                <TrendUp size={14} className="text-emerald-500" weight="bold" />
              ) : (
                <TrendDown size={14} className="text-red-500" weight="bold" />
              )}
              <span className={`text-[11px] font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend >= 0 ? '+' : ''}{trend}%
              </span>
              {trendLabel && <span className="text-[11px] text-[#9c9db7]">{trendLabel}</span>}
            </div>
          )}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: color + '12' }}>
          <Icon size={22} style={{ color }} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl shadow-lg px-3.5 py-2.5">
      <p className="text-[11px] font-semibold text-[#9c9db7] mb-1">{label ? new Date(label).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-[12px] text-[#1d1d1f] font-medium">{p.name}: {formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { activeSeller } = useSellerMode();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [orderTrends, setOrderTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [onboarding, setOnboarding] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { setLoading(true); fetchDashboardData(); }, 100);
    return () => clearTimeout(timer);
  }, [activeSeller?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      const [statsRes, ordersRes, trendsRes, checklistRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/recent-orders?limit=5`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/order-trends?days=7`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/onboarding-checklist`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data);
      setOrderTrends(trendsRes.data);
      if (checklistRes.data) setChecklist(checklistRes.data);
      // Fetch onboarding status
      try {
        const obRes = await axios.get(`${API_URL}/api/seller/onboarding-status`, { withCredentials: true });
        setOnboarding(obRes.data);
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally { setLoading(false); }
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API_URL}/api/seed/demo-data`, {}, { withCredentials: true });
      await fetchDashboardData();
    } catch (error) { console.error('Failed to seed:', error); }
    finally { setSeeding(false); }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Overview of your seller performance">
        <div className="space-y-4">
          <div className="kpi-grid">
            {[1,2,3,4].map(i => <div key={i} className="h-28 skeleton rounded-xl" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 h-72 skeleton rounded-xl" />
            <div className="h-72 skeleton rounded-xl" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isEmpty = !stats?.total_orders && !stats?.total_products;

  return (
    <DashboardLayout
      title="Dashboard"
      subtitle="Overview of your seller performance"
      actions={
        <Button onClick={seedDemoData} disabled={seeding} variant="outline" className="text-sm" data-testid="seed-demo-btn">
          <Lightning size={16} className="mr-2" />
          {seeding ? 'Loading...' : 'Load Demo Data'}
        </Button>
      }
    >
      {isEmpty ? (
        <div className="bg-white border border-[#e5e5ea] rounded-2xl p-8 sm:p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#f2f0ff] flex items-center justify-center">
            <Package size={32} className="text-[#7d64ff]" weight="duotone" />
          </div>
          <h3 className="font-bold text-xl text-[#0A0A0A] mb-2">Welcome to Vinculum</h3>
          <p className="text-[#5a5c87] mb-6 max-w-md mx-auto text-sm">
            Get started by loading demo data to explore all features, or start adding your products and warehouses.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={seedDemoData} disabled={seeding} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="seed-demo-main-btn">
              <Lightning size={18} className="mr-2" />
              {seeding ? 'Loading Demo Data...' : 'Load Demo Data'}
            </Button>
            <Button variant="outline" asChild><Link to="/products">Add Products</Link></Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {/* KPI Cards */}
          <div className="kpi-grid">
            <KpiCard
              icon={Package} label="Total Orders" color="#7d64ff"
              value={stats?.total_orders || 0}
              subtitle={stats?.pending_orders > 0 ? `${stats.pending_orders} pending action` : null}
            />
            <KpiCard
              icon={CurrencyDollar} label="Total Revenue" color="#28cd8c"
              value={formatCurrency(stats?.total_revenue)}
            />
            <KpiCard
              icon={Cube} label="Products" color="#f6a609"
              value={stats?.total_products || 0}
            />
            <KpiCard
              icon={ArrowsClockwise} label="Returns" color="#ea4b5a"
              value={stats?.total_returns || 0}
            />
          </div>

          {/* Getting Started Banner */}
          {onboarding && !onboarding.completed && !onboarding.dismissed && (
            <div className="bg-gradient-to-r from-[#7d64ff]/5 to-[#28cd8c]/5 border border-[#7d64ff]/20 rounded-2xl p-5 flex items-center justify-between" data-testid="onboarding-banner">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#7d64ff]/10 flex items-center justify-center flex-shrink-0">
                  <RocketLaunch size={24} className="text-[#7d64ff]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0A0A0A]">Complete your seller setup</p>
                  <p className="text-xs text-[#6b6b8a]">
                    {Object.values(onboarding.steps || {}).filter(Boolean).length}/4 steps done — Configure TAT, pincodes, slots & store to start receiving orders
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="ghost" size="sm" className="text-xs text-[#9c9db7]" onClick={async () => { try { await axios.post(`${API_URL}/api/seller/onboarding-dismiss`, {}, { withCredentials: true }); setOnboarding(o => ({ ...o, dismissed: true })); } catch {} }} data-testid="dismiss-onboarding">
                  Dismiss
                </Button>
                <Button asChild size="sm" className="bg-[#7d64ff] hover:bg-[#6b4fef] text-xs" data-testid="continue-setup-btn">
                  <Link to="/getting-started">Continue Setup <ArrowRight size={12} className="ml-1" /></Link>
                </Button>
              </div>
            </div>
          )}

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Fulfillment Rate', value: stats?.total_orders ? `${Math.round((stats.delivered_orders || 0) / stats.total_orders * 100)}%` : '0%', icon: Truck, color: '#7d64ff' },
              { label: 'Avg Order Value', value: stats?.total_orders ? formatCurrency((stats.total_revenue || 0) / stats.total_orders) : formatCurrency(0), icon: ChartLine, color: '#28cd8c' },
              { label: 'Active Products', value: stats?.active_products || stats?.total_products || 0, icon: Cube, color: '#f6a609' },
              { label: 'Low Stock Items', value: stats?.low_stock_alerts || 0, icon: Warning, color: stats?.low_stock_alerts > 0 ? '#ea4b5a' : '#9c9db7' },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#e5e5ea] rounded-xl px-3.5 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '10' }}>
                  <s.icon size={16} style={{ color: s.color }} weight="bold" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm sm:text-base font-bold text-[#1d1d1f] truncate">{s.value}</p>
                  <p className="text-[10px] sm:text-[11px] text-[#9c9db7] truncate">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Low Stock Alert */}
          {stats?.low_stock_alerts > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4" data-testid="low-stock-alert">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Warning size={20} className="text-amber-600" weight="fill" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-amber-800 text-sm">{stats.low_stock_alerts} products with low stock</p>
                <p className="text-xs text-amber-600">Review your inventory to prevent stockouts</p>
              </div>
              <Button variant="outline" size="sm" asChild className="border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0">
                <Link to="/inventory">View Inventory</Link>
              </Button>
            </div>
          )}

          {/* Onboarding Checklist */}
          {checklist && checklist.completion_percentage < 100 && (
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-5 sm:p-6" data-testid="onboarding-checklist">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-sm sm:text-base text-[#1d1d1f]">Getting Started</h3>
                <span className="text-xs font-bold text-[#7d64ff] bg-[#f2f0ff] px-3 py-1 rounded-full">
                  {checklist.completion_percentage}% Complete
                </span>
              </div>
              <div className="w-full bg-[#f2f0ff] rounded-full h-2 mb-4">
                <div className="bg-[#7d64ff] h-2 rounded-full transition-all duration-500" style={{ width: `${checklist.completion_percentage}%` }} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                {checklist.steps?.map((step, idx) => (
                  <Link key={idx} to={step.link} className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all ${step.completed ? 'border-emerald-200 bg-emerald-50/50' : 'border-[#e5e5ea] hover:border-[#7d64ff]/40 hover:bg-[#f8f7ff]'}`} data-testid={`checklist-${step.key}`}>
                    {step.completed ? <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" weight="fill" /> : <Circle size={20} className="text-[#d1d1d6] flex-shrink-0" />}
                    <span className={`text-xs sm:text-sm font-medium ${step.completed ? 'text-emerald-700' : 'text-[#5a5c87]'}`}>{step.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Order Trends - Area Chart */}
            <div className="lg:col-span-2 chart-container">
              <div className="flex items-center justify-between mb-4">
                <h3 className="chart-title !mb-0">Order Trends</h3>
                <span className="text-[11px] text-[#9c9db7] font-medium">Last 7 Days</span>
              </div>
              {orderTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={orderTrends}>
                    <defs>
                      <linearGradient id="orderGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7d64ff" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#7d64ff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9c9db7' }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                    <YAxis tick={{ fontSize: 11, fill: '#9c9db7' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="orders" stroke="#7d64ff" strokeWidth={2.5}
                      fill="url(#orderGradient)" dot={{ fill: '#7d64ff', strokeWidth: 2, r: 4, stroke: '#fff' }}
                      activeDot={{ r: 6, stroke: '#7d64ff', strokeWidth: 2, fill: '#fff' }} name="Orders" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-[#9c9db7] text-sm">No order data</div>
              )}
            </div>

            {/* Revenue Bar Chart */}
            <div className="chart-container">
              <div className="flex items-center justify-between mb-4">
                <h3 className="chart-title !mb-0">Revenue</h3>
                <Link to="/analytics" className="text-[11px] text-[#7d64ff] font-semibold hover:underline flex items-center gap-0.5">
                  Details <ArrowUpRight size={10} />
                </Link>
              </div>
              {orderTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={orderTrends.slice(-5)}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7d64ff" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9c9db7' }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-IN', { day: 'numeric' })} />
                    <YAxis tick={{ fontSize: 10, fill: '#9c9db7' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                    <Bar dataKey="revenue" fill="url(#revenueGradient)" radius={[6, 6, 0, 0]} name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-[#9c9db7] text-sm">No revenue data</div>
              )}
            </div>
          </div>

          {/* Recent Orders */}
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">Recent Orders</h3>
              <Button variant="ghost" size="sm" asChild className="text-[#7d64ff] text-xs font-semibold">
                <Link to="/orders">View All <ArrowRight size={14} className="ml-1" /></Link>
              </Button>
            </div>
            {recentOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#fafafe]">
                      <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#9c9db7] uppercase tracking-wider">Order ID</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#9c9db7] uppercase tracking-wider">Customer</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#9c9db7] uppercase tracking-wider">Amount</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#9c9db7] uppercase tracking-wider hidden sm:table-cell">Mode</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#9c9db7] uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.order_id} className="border-t border-[#f0f0f0] hover:bg-[#fafafe] transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-mono text-[13px] font-medium text-[#1d1d1f]">{order.marketplace_order_id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[13px] font-medium text-[#1d1d1f]">{order.customer_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[13px] font-semibold text-[#1d1d1f]">{formatCurrency(order.total_amount)}</span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9c9db7] bg-[#f5f5f7] px-2 py-1 rounded">
                            {order.fulfillment_mode}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={order.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <Package size={40} className="empty-state-icon" weight="duotone" />
                <h4 className="empty-state-title">No orders yet</h4>
                <p className="empty-state-description">Orders will appear here once you start receiving them</p>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
